import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useMruCountry } from "./MruCountryContext";
import type { PhoneCountry } from "./types";
import { useResolvedCountry } from "./useResolvedCountry";
import { digitsOnly, getDialCode, parseE164 } from "./utils/phoneUtils";

type UsePhoneInputProps = {
  value: string;
  onChange: (value: string) => void;
  defaultCountry: PhoneCountry;
};

export function usePhoneInput({
  value,
  onChange,
  defaultCountry,
}: UsePhoneInputProps) {
  const { country: mruCountry, setCountry: setMruCountry } = useMruCountry();
  const inputRef = useRef<HTMLInputElement>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const parsed = useMemo(() => parseE164(value), [value]);
  const country = useResolvedCountry(
    parsed.country,
    mruCountry,
    defaultCountry,
  );
  const nationalDigits = parsed.national;

  const isValid = nationalDigits.length > 0;

  const handleChange = useCallback(
    (next: string) => {
      // Full E.164 paste — may carry a different country code.
      if (next.startsWith("+")) {
        const parsedNext = parseE164(next);
        if (parsedNext.country) {
          if (parsedNext.country !== country) setMruCountry(parsedNext.country);
          const nextE164 = parsedNext.national
            ? `${getDialCode(parsedNext.country)}${parsedNext.national}`
            : "";
          if (nextE164 !== value) onChange(nextE164);
          return;
        }
      }

      const cleaned = digitsOnly(next);
      const dialCodeDigits = getDialCode(country).slice(1);
      const normalizedDigits = cleaned.startsWith(dialCodeDigits)
        ? cleaned.slice(dialCodeDigits.length)
        : cleaned;

      if (normalizedDigits === nationalDigits) return;

      onChange(
        normalizedDigits ? `${getDialCode(country)}${normalizedDigits}` : "",
      );
    },
    [nationalDigits, country, value, setMruCountry, onChange],
  );

  const handleSelectCountry = useCallback(
    (picked: PhoneCountry) => {
      if (picked !== country) {
        setMruCountry(picked);
        onChange(
          nationalDigits ? `${getDialCode(picked)}${nationalDigits}` : "",
        );
      }
    },
    [country, nationalDigits, setMruCountry, onChange],
  );

  // Intercepts paste so that pasting a full E.164 string (e.g. from contacts)
  // always replaces the value cleanly, regardless of cursor position.
  // Without this, pasting "+9641234567" mid-value yields "123+9641234567",
  // which doesn't start with "+" and bypasses E.164 detection in handleChange.
  useEffect(() => {
    const input = inputRef.current;
    if (!input) return;

    const handlePaste = (e: ClipboardEvent) => {
      e.preventDefault();
      handleChange(e.clipboardData?.getData("text") ?? "");
    };

    input.addEventListener("paste", handlePaste);
    return () => input.removeEventListener("paste", handlePaste);
  }, [handleChange]);

  return {
    inputRef,
    country,
    nationalDigits,
    isValid,
    drawerOpen,
    setDrawerOpen,
    handleChange,
    handleSelectCountry,
  };
}
